// ============================================================
// CLUBHUB – Spenden: der Text der Zuwendungsbestätigung
// ============================================================
// Nach den verbindlichen Mustern des BMF fuer Koerperschaften nach
// § 5 Abs. 1 Nr. 9 KStG. EINE Quelle fuer das Blatt im Browser
// (`components/spenden/belegblatt.tsx`) und das PDF
// (`beleg-pdf.tsx`) — damit beide nie auseinanderlaufen.
//
// Saetze mit eingesetzten Angaben sind Folgen von `Stueck`: Text
// oder ein Wert mit Namen; fehlt der Wert, zeigt das Blatt eine rote
// Luecke („[Finanzamt fehlt]“) statt einer leeren Stelle.
//
// Von den beiden Varianten der Steuerbeguenstigung steht die
// zutreffende da: Freistellungsbescheid bzw. Anlage zum
// KSt-Bescheid ODER Feststellung nach § 60a AO (`Aussteller.basis`).
// Fuer einen Sportverein entfaellt „/Mitgliedsbeitrag“: Beitraege an
// Vereine, die den Sport foerdern, sind nicht abziehbar (§ 10b Abs. 1
// Satz 8 EStG). Rein und browsertauglich.
// ============================================================
import { formatDate } from "@/lib/utils";
import { berlinTag } from "./format";
import type { Anschrift, Aussteller, Belegform, BestaetigungView, Posten, Sachangaben } from "./views";

export type BelegblattDaten = {
  form: Belegform;
  nummer: string | null;
  aussteller: Aussteller;
  empfaenger: Anschrift;
  betrag: number;
  /** Tag der Zuwendung (einzeln) bzw. Zeitraum (Sammel). */
  von: string;
  bis: string;
  /** Einzelbestätigung über Geld: handelt es sich um einen Aufwandsverzicht? */
  verzicht: boolean;
  sach: Sachangaben | null;
  posten: Posten[];
  ausgestelltAm: string;
  widerrufen?: { am: string; grund: string | null; ersetztDurch: string | null } | null;
  vorschau?: boolean;
};

export const BELEG_TITEL: Record<Belegform, string> = {
  GELD: "Bestätigung über Geldzuwendungen",
  SACH: "Bestätigung über Sachzuwendungen",
  SAMMEL: "Sammelbestätigung über Geldzuwendungen",
};

export const BELEG_UNTERTITEL =
  "im Sinne des § 10b des Einkommensteuergesetzes an eine der in § 5 Abs. 1 Nr. 9 des Körperschaftsteuergesetzes bezeichneten Körperschaften, Personenvereinigungen oder Vermögensmassen";

export type Stueck = string | { wert: string | null; was: string };

/** „Förderung des Sports (…)“ → „des Sports (…)“ — der Mustertext beginnt schon mit „Förderung“. */
export function zweckNachFoerderung(zweck: string | null): string | null {
  if (!zweck) return null;
  return zweck.replace(/^\s*(die\s+)?Förderung\s+/i, "");
}

function finanzamt(a: Aussteller): string | null {
  return a.taxOffice?.replace(/^\s*Finanzamt\s+/i, "") || null;
}

/** Der Satz zur Steuerbegünstigung — angekreuzt, die andere Variante entfällt. */
export function befreiungsSatz(a: Aussteller): Stueck[] {
  const datum = { wert: a.noticeDate ? formatDate(a.noticeDate) : null, was: "Bescheiddatum" };
  if (a.basis === "FESTSTELLUNG_60A") {
    return [
      "Die Einhaltung der satzungsmäßigen Voraussetzungen nach den §§ 51, 59, 60 und 61 AO wurde vom Finanzamt ",
      { wert: finanzamt(a), was: "Finanzamt" },
      ", StNr. ",
      { wert: a.taxNumber, was: "Steuernummer" },
      ", mit Bescheid vom ",
      datum,
      " nach § 60a AO gesondert festgestellt. Wir fördern nach unserer Satzung folgende Zwecke: ",
      { wert: a.purpose, was: "Begünstigter Zweck" },
      ".",
    ];
  }
  return [
    "Wir sind wegen Förderung ",
    { wert: zweckNachFoerderung(a.purpose), was: "Begünstigter Zweck" },
    " nach dem Freistellungsbescheid bzw. nach der Anlage zum Körperschaftsteuerbescheid des Finanzamtes ",
    { wert: finanzamt(a), was: "Finanzamt" },
    ", StNr. ",
    { wert: a.taxNumber, was: "Steuernummer" },
    ", vom ",
    datum,
    " für den letzten Veranlagungszeitraum ",
    { wert: a.period, was: "Veranlagungszeitraum" },
    " nach § 5 Abs. 1 Nr. 9 des Körperschaftsteuergesetzes von der Körperschaftsteuer und nach § 3 Nr. 6 des Gewerbesteuergesetzes von der Gewerbesteuer befreit.",
  ];
}

export function verwendungsSatz(a: Aussteller): Stueck[] {
  return ["Es wird bestätigt, dass die Zuwendung nur zur Förderung ", { wert: zweckNachFoerderung(a.purpose), was: "Begünstigter Zweck" }, " verwendet wird."];
}

export const SACH_HERKUNFT = {
  BETRIEB:
    "Die Sachzuwendung stammt nach den Angaben des Zuwendenden aus dem Betriebsvermögen. Die Zuwendung wurde nach dem Wert der Entnahme (ggf. mit dem niedrigeren gemeinen Wert) und nach der Umsatzsteuer, die auf die Entnahme entfällt, bewertet.",
  PRIVAT: "Die Sachzuwendung stammt nach den Angaben des Zuwendenden aus dem Privatvermögen.",
  UNBEKANNT: "Der Zuwendende hat trotz Aufforderung keine Angaben zur Herkunft der Sachzuwendung gemacht.",
  UNTERLAGEN: "Geeignete Unterlagen, die zur Wertermittlung gedient haben, z. B. Rechnung, Gutachten, liegen vor.",
} as const;

export const SAMMEL_SAETZE = [
  "Es wird bestätigt, dass über die in der Gesamtsumme enthaltenen Zuwendungen keine weiteren Bestätigungen, weder formelle Zuwendungsbestätigungen noch Beitragsquittungen oder Ähnliches ausgestellt wurden und werden.",
  "Ob es sich um den Verzicht auf Erstattung von Aufwendungen handelt, ist der Anlage zur Sammelbestätigung zu entnehmen.",
] as const;

export const HINWEIS_SAETZE = [
  "Wer vorsätzlich oder grob fahrlässig eine unrichtige Zuwendungsbestätigung erstellt oder veranlasst, dass Zuwendungen nicht zu den in der Zuwendungsbestätigung angegebenen steuerbegünstigten Zwecken verwendet werden, haftet für die entgangene Steuer (§ 10b Abs. 4 EStG, § 9 Abs. 3 KStG, § 9 Nr. 5 GewStG).",
  "Diese Bestätigung wird nicht als Nachweis für die steuerliche Berücksichtigung der Zuwendung anerkannt, wenn das Datum des Freistellungsbescheides länger als 5 Jahre bzw. das Datum der Feststellung der Einhaltung der satzungsmäßigen Voraussetzungen nach § 60a Abs. 1 AO länger als 3 Jahre seit Ausstellung des Bescheides zurückliegt (§ 63 Abs. 5 AO).",
] as const;

/** Eine ausgestellte Bestätigung als Daten fuer das Blatt — mit den eingefrorenen Angaben. */
export function belegDaten(b: BestaetigungView): BelegblattDaten {
  return {
    form: b.form,
    nummer: b.number,
    aussteller: b.aussteller,
    empfaenger: b.empfaenger,
    betrag: b.totalAmount,
    von: b.periodFrom,
    bis: b.periodTo,
    verzicht: b.kind === "WAIVER",
    sach: b.sach,
    posten: b.posten,
    ausgestelltAm: b.ausgestelltAm ?? berlinTag(b.issuedAt),
    widerrufen: b.cancelledAt ? { am: b.cancelledAt, grund: b.cancelReason, ersetztDurch: b.replacedByNumber } : null,
  };
}

/** Dateiname des PDFs — Nummer, kein Name (der steht im Dokument, nicht im Dateisystem). */
export function belegDateiname(nummer: string, widerrufen = false): string {
  return `Zuwendungsbestätigung ${nummer}${widerrufen ? " (widerrufen)" : ""}.pdf`;
}
